import apiPath from "./apiPath";

function isNativeWebView() {
    return (
        typeof window !== "undefined" &&
        typeof window.ReactNativeWebView?.postMessage === "function"
    );
}

function getNativeDeviceId() {
    return window.__FTC_NATIVE__?.deviceId ?? null;
}

function normalizeRole(role) {
    return role === "Provider" || role === "provider" ? "provider" : "user";
}

function postNativeMessage(message) {
    if (!isNativeWebView()) return;
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
}

/** Notify the native shell that auth is ready so it can register push tokens. */
export function notifyNativeAuthReady({ refreshToken, role }) {
    if (!refreshToken) return;
    postNativeMessage({
        type: "AUTH_READY",
        payload: {
            refreshToken,
            role: normalizeRole(role) === "provider" ? "provider" : "user",
        },
    });
}

/**
 * Publish the current website auth session to the native shell.
 * Runs on app load (after redux-persist rehydrates) and when auth changes.
 */
export function syncNativeAuthState({ isLoggedIn, refreshToken, role }) {
    if (!isNativeWebView()) {
        return undefined;
    }

    const publish = () => {
        if (isLoggedIn && refreshToken) {
            notifyNativeAuthReady({ refreshToken, role });
            return;
        }

        postNativeMessage({
            type: "AUTH_SYNC",
            payload: { isLoggedIn: false },
        });
    };

    publish();

    window.addEventListener("ftc-native-ready", publish);
    return () => window.removeEventListener("ftc-native-ready", publish);
}

/** Ask the native shell to clear its session after web-side device cleanup. */
export function notifyNativeLogout({
    refreshToken,
    role,
    unauthorized = false,
    deviceUnregistered = true,
}) {
    postNativeMessage({
        type: unauthorized ? "UNAUTHORIZED" : "LOGOUT",
        payload: {
            refreshToken: refreshToken ?? undefined,
            role: role ? normalizeRole(role) : undefined,
            deviceUnregistered,
        },
    });
}

/**
 * Remove this native device's push token from the backend.
 * Only runs inside the FTC mobile WebView (browser sessions have no device_id).
 */
export async function unregisterNativePushDevice({
    refreshToken,
    accessToken,
    role,
}) {
    if (!isNativeWebView()) return;

    const deviceId = getNativeDeviceId();
    const authToken = refreshToken || accessToken;
    if (!deviceId || !authToken) return;

    const path =
        normalizeRole(role) === "provider"
            ? apiPath.spUnregisterDevice
            : apiPath.userUnregisterDevice;

    try {
        await fetch(path, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ device_id: deviceId }),
        });
    } catch {
        // Best-effort cleanup
    }
}

export function isRunningInNativeApp() {
    return isNativeWebView();
}
